// Calendario personal del empleado — asistencia + licencias + feriados.
// Click en día libre → modal para pedir Art 42 directo.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, IconButton, Tooltip, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Chip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { nodeApiPath } from '../../utils/paths';
import { triggerSessionExpired } from '../../api/endpoints';

const BR = '4px';
const PRIMARY = '#0277bd';

async function jf(path, options = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No token'); }
    const res = await fetch(nodeApiPath(path), {
        ...options,
        headers: {
            'Accept': 'application/json',
            ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function MiCalendario({ resumen, onSolicitudCreada, notify }) {
    const hoy = new Date();
    const [mes, setMes] = useState(hoy.getMonth() + 1);
    const [ano, setAno] = useState(hoy.getFullYear());
    const [data, setData] = useState({ asistencia: [], licencias: [], feriados: [] });
    const [loading, setLoading] = useState(true);
    const [art42Dlg, setArt42Dlg] = useState({ open: false, fecha: '', saving: false });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await jf(`personal/mi/calendario?mes=${mes}&ano=${ano}`);
            if (r.ok) setData({ asistencia: r.asistencia || [], licencias: r.licencias || [], feriados: r.feriados || [] });
        } catch { }
        setLoading(false);
    }, [mes, ano]);

    useEffect(() => { cargar(); }, [cargar]);

    const cambiarMes = (delta) => {
        let m = mes + delta; let a = ano;
        if (m < 1) { m = 12; a--; }
        if (m > 12) { m = 1; a++; }
        setMes(m); setAno(a);
    };

    // Armar mapa por día
    const totalDias = new Date(ano, mes, 0).getDate();
    const primerDow = new Date(ano, mes - 1, 1).getDay();
    const feriadosMap = new Map(data.feriados.map(f => [f.Fecha, f.Motivo]));
    const asistenciaMap = new Map(data.asistencia.map(a => [a.Fecha, a]));
    const licenciasPorDia = {};
    for (const l of data.licencias) {
        const start = new Date(l.Fecha_Desde + 'T12:00:00');
        const end = new Date(l.Fecha_Hasta + 'T12:00:00');
        for (let d = 1; d <= totalDias; d++) {
            const fechaIso = `${ano}-${String(mes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const dt = new Date(ano, mes - 1, d);
            if (dt >= start && dt <= end) {
                if (!licenciasPorDia[fechaIso]) licenciasPorDia[fechaIso] = [];
                licenciasPorDia[fechaIso].push(l);
            }
        }
    }

    const colorEstado = (estado) => ({
        Normal: { bg: '#e8f5e9', color: '#2e7d32' },
        Tardanza: { bg: '#fff3e0', color: '#e65100' },
        Ausencia: { bg: '#ffebee', color: '#c62828' },
        Incompleto: { bg: '#e3f2fd', color: '#1565c0' },
        Licencia: { bg: '#e0f2f1', color: '#00695c' },
        'Art.42': { bg: '#f3e5f5', color: '#6a1b9a' },
        Suspendido: { bg: '#fbe9e7', color: '#d84315' },
    }[estado] || { bg: '#f5f5f5', color: '#666' });

    const handleDiaClick = (fechaIso, esFinde, esFeriado, tieneLicencia) => {
        if (esFinde || esFeriado || tieneLicencia) return;
        const dt = new Date(fechaIso + 'T12:00:00');
        const hoyDt = new Date(); hoyDt.setHours(0, 0, 0, 0);
        if (dt < hoyDt) return; // No permitir solicitar en pasado
        setArt42Dlg({ open: true, fecha: fechaIso, saving: false });
    };

    const confirmarArt42 = async () => {
        setArt42Dlg(p => ({ ...p, saving: true }));
        try {
            const r = await jf('personal/mi/solicitudes', {
                method: 'POST',
                body: JSON.stringify({
                    Tipo: 'ART42', Fecha_Desde: art42Dlg.fecha, Fecha_Hasta: art42Dlg.fecha,
                    Observaciones: 'Solicitado desde el calendario',
                }),
            });
            if (r.ok) {
                notify?.('Art. 42 solicitado', 'success');
                setArt42Dlg({ open: false, fecha: '', saving: false });
                cargar();
                onSolicitudCreada?.();
            } else notify?.(r.mensaje || 'Error', 'error');
        } catch (e) { notify?.('Error: ' + e.message, 'error'); }
        setArt42Dlg(p => ({ ...p, saving: false }));
    };

    return (
        <Box>
            {/* Header navegación */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton onClick={() => cambiarMes(-1)} size="small"><ChevronLeftIcon /></IconButton>
                <Typography variant="h6" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, color: PRIMARY }}>
                    {MESES[mes]} {ano}
                </Typography>
                <IconButton onClick={() => cambiarMes(1)} size="small"><ChevronRightIcon /></IconButton>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>
            ) : (
                <>
                    {/* Header días de la semana */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
                        {DIAS_SEMANA.map(d => (
                            <Box key={d} sx={{ textAlign: 'center', py: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: d === 'Dom' || d === 'Sáb' ? '#999' : '#333' }}>{d}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Grilla de días */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                        {Array.from({ length: primerDow }).map((_, i) => <Box key={`e${i}`} />)}
                        {Array.from({ length: totalDias }).map((_, i) => {
                            const dia = i + 1;
                            const fechaIso = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                            const dow = new Date(ano, mes - 1, dia).getDay();
                            const esFinde = dow === 0 || dow === 6;
                            const esFeriado = feriadosMap.has(fechaIso);
                            const asist = asistenciaMap.get(fechaIso);
                            const licencias = licenciasPorDia[fechaIso] || [];
                            const tieneLicencia = licencias.length > 0;
                            const esHoy = fechaIso === new Date().toISOString().substring(0, 10);
                            const esFuturo = new Date(fechaIso + 'T12:00:00') > new Date(new Date().toISOString().substring(0, 10) + 'T12:00:00');

                            // Color del día
                            let tooltip = '';
                            if (esFinde) { tooltip = dow === 0 ? 'Domingo' : 'Sábado'; }
                            else if (esFeriado) { tooltip = `Feriado: ${feriadosMap.get(fechaIso)}`; }
                            else if (tieneLicencia) {
                                const l = licencias[0];
                                tooltip = `${l.Tipo} (${l.Estado})`;
                            } else if (asist) {
                                tooltip = asist.Estado + (asist.Minutos_Tardanza > 0 ? ` — ${asist.Minutos_Tardanza} min tarde` : '');
                            } else if (esFuturo) {
                                tooltip = 'Click para pedir Art. 42';
                            }

                            const c = esFinde ? { bg: '#f5f5f5', color: '#999' }
                                : esFeriado ? { bg: '#f3e5f5', color: '#6a1b9a' }
                                : tieneLicencia ? colorEstado(licencias[0].Tipo === 'ART42' ? 'Art.42' : 'Licencia')
                                : asist ? colorEstado(asist.Estado)
                                : { bg: '#fff', color: '#333' };

                            const clickable = !esFinde && !esFeriado && !tieneLicencia && esFuturo;

                            return (
                                <Tooltip key={dia} title={tooltip || ''} arrow placement="top">
                                    <Paper
                                        onClick={() => clickable && handleDiaClick(fechaIso, esFinde, esFeriado, tieneLicencia)}
                                        sx={{
                                            p: 0.75, borderRadius: BR, minHeight: 70, cursor: clickable ? 'pointer' : 'default',
                                            bgcolor: c.bg, border: esHoy ? `2px solid ${PRIMARY}` : '1px solid #e0e0e0',
                                            opacity: esFinde ? 0.5 : 1,
                                            transition: 'all 0.2s',
                                            '&:hover': clickable ? { boxShadow: `0 2px 8px rgba(0,0,0,0.15)`, transform: 'scale(1.02)' } : {},
                                        }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" sx={{ fontWeight: esHoy ? 700 : 600, color: c.color }}>{dia}</Typography>
                                            {esHoy && <Typography variant="caption" sx={{ fontSize: '0.6rem', color: PRIMARY, fontWeight: 700 }}>HOY</Typography>}
                                        </Box>
                                        {tieneLicencia && (
                                            <Typography variant="caption" sx={{ fontSize: '0.6rem', display: 'block', color: c.color, fontWeight: 600, mt: 0.25 }}>
                                                {licencias[0].Tipo === 'ART42' ? 'Art.42' : licencias[0].Tipo === 'VACACIONES' ? 'Vacac.' : licencias[0].Tipo === 'CARTA_MEDICA' ? 'Carta M.' : 'Licencia'}
                                            </Typography>
                                        )}
                                        {!tieneLicencia && asist?.Estado && !esFinde && !esFeriado && (
                                            <Typography variant="caption" sx={{ fontSize: '0.6rem', display: 'block', color: c.color, fontWeight: 500, mt: 0.25 }}>
                                                {asist.Estado}
                                            </Typography>
                                        )}
                                        {!tieneLicencia && !asist && esFuturo && !esFinde && !esFeriado && (
                                            <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#aaa', display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                                                libre
                                            </Typography>
                                        )}
                                    </Paper>
                                </Tooltip>
                            );
                        })}
                    </Box>

                    {/* Leyenda */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, justifyContent: 'center' }}>
                        {[
                            { label: 'Normal', ...colorEstado('Normal') },
                            { label: 'Tardanza', ...colorEstado('Tardanza') },
                            { label: 'Ausencia', ...colorEstado('Ausencia') },
                            { label: 'Licencia', ...colorEstado('Licencia') },
                            { label: 'Art. 42', ...colorEstado('Art.42') },
                            { label: 'Feriado', bg: '#f3e5f5', color: '#6a1b9a' },
                        ].map(l => (
                            <Chip key={l.label} label={l.label} size="small"
                                sx={{ bgcolor: l.bg, color: l.color, fontSize: '0.7rem', fontWeight: 600, borderRadius: BR }} />
                        ))}
                    </Box>

                    <Alert severity="info" sx={{ mt: 2, borderRadius: BR, fontSize: '0.8rem' }}>
                        Podés hacer click en un día libre futuro para solicitar un Art. 42 directamente desde el calendario.
                    </Alert>
                </>
            )}

            {/* Dialog Art 42 */}
            <Dialog open={art42Dlg.open} onClose={() => setArt42Dlg({ open: false, fecha: '', saving: false })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventAvailableIcon /> Solicitar Art. 42
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="body2">
                        Vas a solicitar un Art. 42 para el día <strong>{art42Dlg.fecha && new Date(art42Dlg.fecha + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2, borderRadius: BR, fontSize: '0.8rem' }}>
                        Tenés {resumen?.art42?.disponiblesAno || 0} días disponibles este año (máx 2 por mes).
                        El pedido irá a RRHH y luego a Secretaría General.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setArt42Dlg({ open: false, fecha: '', saving: false })} sx={{ textTransform: 'none' }} disabled={art42Dlg.saving}>Cancelar</Button>
                    <Button onClick={confirmarArt42} variant="contained" disabled={art42Dlg.saving}
                        sx={{ bgcolor: '#6a1b9a', textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: '#4a148c' } }}>
                        {art42Dlg.saving ? 'Enviando...' : 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
